/**
 * UI state machine - replaces useUIStore (Zustand)
 * Manages loading, confirm dialog, media viewer
 */
import { setup, assign } from 'xstate'

export interface ConfirmData {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  icon?: 'warning' | 'error' | 'info' | 'success' | 'question'
}

export type MediaViewerDataType = 'image' | 'video' | 'audio' | 'document'

export interface MediaViewerData {
  type: MediaViewerDataType
  url: string
  title?: string
  thumbnailUrl?: string
  mimeType?: string
  size?: number
  items?: MediaViewerItem[]
  currentIndex?: number
}

export interface MediaViewerItem {
  id: string
  type: MediaViewerDataType
  url: string
  title?: string
  thumbnailUrl?: string
}

export interface UIContext {
  isLoading: boolean
  loadingMessage?: string
  isBundleLoading: boolean
  confirmData: ConfirmData | null
  confirmResolve: ((result: boolean) => void) | null
  mediaViewerData: MediaViewerData | null
}

export type UIEvent =
  | { type: 'SET_LOADING'; loading: boolean; message?: string }
  | { type: 'SET_BUNDLE_LOADING'; loading: boolean }
  | { type: 'SHOW_CONFIRM'; data: ConfirmData; resolve: (result: boolean) => void }
  | { type: 'CLOSE_CONFIRM'; confirmed: boolean }
  | { type: 'OPEN_MEDIA_VIEWER'; data: MediaViewerData }
  | { type: 'CLOSE_MEDIA_VIEWER' }

const machineSetup = setup({
  types: {
    context: {} as UIContext,
    events: {} as UIEvent,
  },
})

export const uiMachine = machineSetup.createMachine({
  id: 'ui',
  context: {
    isLoading: false,
    loadingMessage: undefined,
    isBundleLoading: false,
    confirmData: null,
    confirmResolve: null,
    mediaViewerData: null,
  },
  on: {
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
        loadingMessage: ({ event }) => (event.type === 'SET_LOADING' ? event.message : undefined),
      }),
    },
    SET_BUNDLE_LOADING: {
      actions: assign({
        isBundleLoading: ({ event }) =>
          event.type === 'SET_BUNDLE_LOADING' ? event.loading : false,
      }),
    },
    SHOW_CONFIRM: {
      actions: assign({
        confirmData: ({ event }: { event: UIEvent }) => (event.type === 'SHOW_CONFIRM' ? event.data : null),
        confirmResolve: ({ event }: { event: UIEvent }) => (event.type === 'SHOW_CONFIRM' ? event.resolve : null),
      }),
    },
    CLOSE_CONFIRM: {
      actions: [
        ({ context, event }: { context: UIContext; event: UIEvent }) => {
          if (event.type === 'CLOSE_CONFIRM' && context.confirmResolve) {
            context.confirmResolve(event.confirmed)
          }
        },
        assign({ confirmData: null, confirmResolve: null }),
      ],
    },
    OPEN_MEDIA_VIEWER: {
      actions: assign({
        mediaViewerData: ({ event }) => (event.type === 'OPEN_MEDIA_VIEWER' ? event.data : null),
      }),
    },
    CLOSE_MEDIA_VIEWER: {
      actions: assign({ mediaViewerData: null }),
    },
  },
})
